import {
  IonButtons,
  IonContent,
  IonHeader,
  IonModal,
  IonTitle,
  IonToggle,
  IonToolbar,
} from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { SPICINESS_LEVELS } from '../../constants/tags';
import { useTagsRegistry } from '../../hooks/useTagsRegistry';
import type { SearchLocalListingsParams, SearchLocalListingsFilters } from '../../services/listingService';
import { SelectableChip } from '../ui/SelectableChip';

export type TimeRangePreset = 'today' | 'tomorrow' | 'thisWeek' | 'all';

// Ordered list of time-range presets. Labels live in the `filter` i18n
// namespace as `window.<preset>`. HomePage imports this same list to
// render its chip strip, so the modal and the strip stay in lockstep
// when a preset is added.
export const TIME_RANGE_PRESETS: TimeRangePreset[] = ['today', 'tomorrow', 'thisWeek', 'all'];

export interface HomeFeedFilterState {
  filters: SearchLocalListingsFilters;
  orderBy?: SearchLocalListingsParams['orderBy'];
  order?: SearchLocalListingsParams['order'];
  timeRange: TimeRangePreset;
}

interface HomeFeedFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  value: HomeFeedFilterState;
  onChange: (state: HomeFeedFilterState) => void;
}

function toggleInList(list: string[], item: string): string[] {
  return list.includes(item) ? list.filter((v) => v !== item) : [...list, item];
}

function toggleInNumberList(list: number[], item: number): number[] {
  return list.includes(item) ? list.filter((v) => v !== item) : [...list, item];
}

function nonEmpty<T>(arr: T[]): T[] | undefined {
  return arr.length > 0 ? arr : undefined;
}

export const EMPTY_FILTER_STATE: HomeFeedFilterState = {
  filters: {},
  timeRange: 'tomorrow',
  orderBy: 'windowStart',
  order: 'asc',
};

export const HomeFeedFilterModal: React.FC<HomeFeedFilterModalProps> = ({
  isOpen,
  onClose,
  value,
  onChange,
}) => {
  // `filter` namespace = section headers, time-window / fulfillment /
  // spice labels, buttons. Tag chip labels (origins / dietary / allergens)
  // come from the BE registry via useTagsRegistry — single source of truth
  // across publish + filter surfaces. (Sort lives in HomeFeedSortPill.)
  const { t } = useTranslation('filter');
  const { data: tags } = useTagsRegistry();
  const { filters, timeRange } = value;
  const dietaryInclude = filters.dietaryInclude ?? [];
  const allergenExclude = filters.allergenExclude ?? [];
  const originInclude = filters.originInclude ?? [];
  const spicy = filters.spicy ?? [];

  const update = (patch: Partial<SearchLocalListingsFilters>) => {
    onChange({ ...value, filters: { ...filters, ...patch } });
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose}>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <button
              type="button"
              onClick={onClose}
              className="px-4 text-sm font-medium text-brand-primary"
            >
              {t('done')}
            </button>
          </IonButtons>
          <IonTitle className="ion-text-center">{t('title')}</IonTitle>
          <IonButtons slot="end">
            <button
              type="button"
              onClick={() => onChange(EMPTY_FILTER_STATE)}
              className="px-4 text-sm font-medium text-brand-primary"
            >
              {t('reset')}
            </button>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="p-6 space-y-6">

          {/* Availability */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold uppercase text-muted-foreground">{t('availability')}</h3>
            <div className="flex flex-wrap gap-2">
              {TIME_RANGE_PRESETS.map((key) => (
                <SelectableChip
                  key={key}
                  active={timeRange === key}
                  onClick={() => onChange({ ...value, timeRange: key })}
                >
                  {t(`window.${key}`)}
                </SelectableChip>
              ))}
            </div>
          </div>

          {/* Dietary — include. Tag values + labels come from the BE
              registry. The wire-format `value` stays the filter API
              parameter; the registry's `label` is the chip display string. */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold uppercase text-muted-foreground">
              {t('dietaryInclude')}
            </h3>
            <div className="flex flex-wrap gap-2">
              {tags?.dietary.map((tag) => {
                const active = dietaryInclude.includes(tag.value);
                return (
                  <SelectableChip
                    key={tag.value}
                    active={active}
                    onClick={() => update({ dietaryInclude: nonEmpty(toggleInList(dietaryInclude, tag.value)) })}
                  >
                    {tag.label}
                  </SelectableChip>
                );
              })}
            </div>
          </div>

          {/* Allergens — exclude */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold uppercase text-muted-foreground">
              {t('allergenExclude')}
            </h3>
            <div className="flex flex-wrap gap-2">
              {tags?.allergens.map((tag) => {
                const active = allergenExclude.includes(tag.value);
                return (
                  <SelectableChip
                    key={tag.value}
                    active={active}
                    onClick={() => update({ allergenExclude: nonEmpty(toggleInList(allergenExclude, tag.value)) })}
                  >
                    {tag.label}
                  </SelectableChip>
                );
              })}
            </div>
          </div>

          {/* Cuisine */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold uppercase text-muted-foreground">{t('cuisine')}</h3>
            <div className="flex flex-wrap gap-2">
              {tags?.origins.map((tag) => {
                const active = originInclude.includes(tag.value);
                return (
                  <SelectableChip
                    key={tag.value}
                    active={active}
                    onClick={() => update({ originInclude: nonEmpty(toggleInList(originInclude, tag.value)) })}
                  >
                    {tag.label}
                  </SelectableChip>
                );
              })}
            </div>
          </div>

          {/* Spicy level */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold uppercase text-muted-foreground">{t('spiceLevel')}</h3>
            <div className="flex flex-wrap gap-2">
              {SPICINESS_LEVELS.map((level) => {
                const active = spicy.includes(level);
                return (
                  <SelectableChip
                    key={level}
                    active={active}
                    onClick={() => update({ spicy: nonEmpty(toggleInNumberList(spicy, level)) })}
                  >
                    {level === 0 ? t('spice.notSpicy') : '🌶️'.repeat(level)}
                  </SelectableChip>
                );
              })}
            </div>
          </div>

          {/* Hide sold out. Off by default: sold-out listings show in the feed
              (sorted last) so they don't silently vanish once portions run out. */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">{t('hideSoldOut')}</span>
            <IonToggle
              checked={filters.hideSoldOut === true}
              onIonChange={(e) =>
                update({ hideSoldOut: e.detail.checked ? true : undefined })
              }
            />
          </div>
        </div>
      </IonContent>
    </IonModal>
  );
};
